<script setup lang="ts">
import { userValidateCardCreate } from "@innoclouds/api";
import type { AxiosInstance } from "axios";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useTranslation } from "i18next-vue";
import { onUnmounted, ref } from "vue";
import { useUserStore } from "#/store/user";
import QR from "../../assets/iconsDynamic/QR.vue";
import cameraLanguage from "./qrCameraScanner.ts";

const props = defineProps<{
	axiosInstanceGet?: AxiosInstance;
	is_toggleAffiliation?: boolean;
	is_notification?: boolean;
	notificationData?: { textNotification: string };
	isQr?: boolean | null;
	takeDataTable?: (orgLinkId: string, tableId: string, source?: string) => Promise<void> | undefined;
}>();

const { t } = useTranslation();
const userStore = useUserStore();

let html5QrCode: Html5Qrcode | null = null;
const isScanning = ref<boolean>(false);
const isCameraReady = ref<boolean>(false);
const scanCooldown = ref<boolean>(false);
const isProcessingScan = ref<boolean>(false);
let cooldownTimer: ReturnType<typeof setTimeout> | null = null;
const emit = defineEmits<{
	(e: "update:notificationData", value: { textNotification: string }): void;
	(e: "update:is_notification", value: boolean): void;
	(e: "update:is_toggleAffiliation", value: boolean): void;
}>();

const normalizeCardNumber = (raw: string): string => {
	return raw.replace(/^[\s\S]*?((?:tel|sms|smsto|mailto|callto|facetime|viber|whatsapp):)?[\s\S]*?(%2B)?[\s\S]*?/i, (_, __, pct) => (pct ? "+" : ""));
};

const onScanSuccess = async (decodedText: string, decodedResult?: any) => {
	if (isProcessingScan.value) return;
	if (!props.isQr) isProcessingScan.value = true;
	stopCamera();

	const regex = /[?&]table_id=([a-zA-Z0-9-]+)/;
	const match = decodedText.match(regex);
	const orgLinkIdMatch = decodedText.match(/\/([a-f0-9-]{36})\?/);

	if (props.isQr) {
		if (match && orgLinkIdMatch) {
			return props.takeDataTable?.(orgLinkIdMatch[1], match[1], "qr");
		} else {
			const orgLinkId = localStorage.getItem("orgID");
			if (orgLinkId) {
				const tableId = await props.axiosInstanceGet
					?.get(`org_unit/${orgLinkId}/table`, { params: { external_link: decodedText } })
					.then((response: any) => (response.data?.length > 0 ? response.data[0]?.id : null))
					.catch(() => null);

				if (tableId) {
					return props.takeDataTable?.(orgLinkId, tableId);
				}
			}
		}
		emit("update:is_notification", true);
		emit("update:notificationData", { textNotification: t(`camera-scanner.notification.table-not-found`) });
		emit("update:is_toggleAffiliation", true);
	} else {
		const decodedGeneralText = decodedText ? decodedText : decodedResult.text;
		const cardNumber = normalizeCardNumber(decodedGeneralText);
		try {
			const user = await userValidateCardCreate({ card_number: cardNumber.trim() }).then(userStore.auth);

			const balance = user?.balance ?? 0;
			const name = user?.first_name;
			const message =
				balance > 0
					? t(`camera-scanner.notification.welcome-message-with-balance`, { name, balance })
					: t(`camera-scanner.notification.welcome-message`, { name });
			emit("update:is_notification", true);
			emit("update:notificationData", { textNotification: message });
		} catch (e: any) {
			emit("update:is_notification", true);
			emit("update:notificationData", { textNotification: e.response?.user_friendly_message || t(`camera-scanner.notification.card-not-found`) });
		} finally {
			scanCooldown.value = true;
			if (cooldownTimer) clearTimeout(cooldownTimer);
			cooldownTimer = setTimeout(() => {
				scanCooldown.value = false;
			}, 2000);
			isProcessingScan.value = false;
		}
	}
};
// для дебага qr-сканирования
(window as any).debugScan = onScanSuccess;

const onScanFailure = () => {};

const openCamera = async () => {
	if (isScanning.value || html5QrCode?.isScanning || scanCooldown.value) return;

	try {
		isScanning.value = true;
		try {
			const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
			tempStream.getTracks().forEach((track) => {
				track.stop();
			});
		} catch (permError) {
			emit("update:is_notification", true);
			emit("update:notificationData", { textNotification: t(`camera-scanner.notification.camera-access`) });
			isScanning.value = false;
			emit("update:is_toggleAffiliation", true);
			return;
		}
		html5QrCode = new Html5Qrcode("reader");
		const devices = await Html5Qrcode.getCameras();
		if (!devices || devices.length === 0) {
			console.error("Камеры не найдены");
			isScanning.value = false;
			return;
		}

		let selectedCamera: any;

		if (props.isQr) {
			selectedCamera = devices.find((device) => cameraLanguage.back.some((language) => new RegExp(language, "i").test(device.label)));

			if (!selectedCamera) {
				selectedCamera = devices.find((device) => cameraLanguage.front.some((language) => new RegExp(language, "i").test(device.label)));
			}
		} else {
			selectedCamera = devices.find((device) => cameraLanguage.front.some((language) => new RegExp(language, "i").test(device.label)));

			if (!selectedCamera) {
				selectedCamera = devices.find((device) => /amera/i.test(device.label));
			}

			if (!selectedCamera) {
				emit("update:is_notification", true);
				emit("update:notificationData", {
					textNotification: t(`camera-scanner.notification.no-front-camera`),
				});
				isScanning.value = false;
				emit("update:is_toggleAffiliation", true);
				return;
			}
		}

		const formats = props.isQr
			? [Html5QrcodeSupportedFormats.QR_CODE]
			: [
					Html5QrcodeSupportedFormats.QR_CODE,
					Html5QrcodeSupportedFormats.PDF_417,
					Html5QrcodeSupportedFormats.EAN_13,
					Html5QrcodeSupportedFormats.EAN_8,
					Html5QrcodeSupportedFormats.CODE_128,
				];

		const config = { fps: 60, qrbox: { width: 320, height: 500 } };

		await (html5QrCode as any).start(selectedCamera.id, config, onScanSuccess, onScanFailure, undefined, formats);

		isCameraReady.value = true;
	} catch (err) {
		emit("update:is_notification", true);
		emit("update:notificationData", { textNotification: t(`camera-scanner.notification.camera-access`) });
		console.error("Ошибка доступа к камере:", err);
		isScanning.value = false;
		isCameraReady.value = false;
		emit("update:is_toggleAffiliation", true);
	}
};

const stopCamera = async () => {
	if (html5QrCode && html5QrCode.isScanning) {
		try {
			await html5QrCode.stop();
			isScanning.value = false;
			html5QrCode = null;
		} catch (err) {
			console.error("Ошибка остановки камеры:", err);
		}
	} else {
		isScanning.value = false;
		html5QrCode = null;
		emit("update:is_toggleAffiliation", true);
	}
	isCameraReady.value = false;
};

onUnmounted(() => {
	if (isScanning.value) {
		stopCamera();
	}
	if (cooldownTimer) {
		clearTimeout(cooldownTimer);
		cooldownTimer = null;
	}
	isProcessingScan.value = false;
	scanCooldown.value = false;
	isCameraReady.value = false;
});
</script>

<template>
	<div>
		<Teleport to="body">
			<div class="blackWindow" v-show="isScanning" @click="stopCamera">
				<div id="reader"></div>
				<p v-if="isCameraReady && !isQr" class="qr_text">{{ t(`camera-scanner.title`) }}</p>
			</div>
		</Teleport>
		<QR :color="isQr ? undefined : '#FFFFFF'" @click="openCamera" />
	</div>
</template>

<style lang="scss">
@use './qrCameraScanner.scss';
</style>
